import { useState, useEffect } from 'react'
import DocumentCapture from './DocumentCapture'
import DocumentCaptureSimple from './DocumentCaptureSimple'
import LivenessCheck from './LivenessCheck'
import VerificationResult from './VerificationResult'
import { createVerification, uploadDocumentFront, uploadDocumentBack, uploadSelfie, uploadLivenessGIF, compareFaces, getVerificationStatus } from '../utils/verificationApi'
import { getFingerprint } from '../utils/fingerprint'
import '../styles/design-system.css'
import '../styles/VerificationFlow.css'

const USE_SIMPLE_CAPTURE = false // Toggle para testar

const VerificationFlow = ({
  onComplete,
  onError: onErrorCallback,
  onStepChange,
  onProgress,
  apiKey,
  theme,
  initialUserData
}) => {
  console.log('[FLOW] Component render')

  const [step, setStep] = useState(0)
  const [verificationId, setVerificationId] = useState(null)
  const [documentType, setDocumentType] = useState(null)
  const [documentFrontData, setDocumentFrontData] = useState(null)
  const [documentBackData, setDocumentBackData] = useState(null)
  const [selfieData, setSelfieData] = useState(null)
  const [finalResult, setFinalResult] = useState(null)
  const [error, setError] = useState(null)
  const [fingerprint, setFingerprint] = useState(null)
  const [userData, setUserData] = useState({
    nome: initialUserData?.nome || '',
    cpf: initialUserData?.cpf || ''
  })
  const isLocked = initialUserData?.locked === true
  const [editingField, setEditingField] = useState(null)

  // Consentimentos obrigatórios antes de iniciar a verificação
  const [fraudConsent, setFraudConsent] = useState(false)
  const [policyConsent, setPolicyConsent] = useState(false)
  const consentsGiven = fraudConsent && policyConsent

  // Update userData when initialUserData changes (from parent via postMessage)
  useEffect(() => {
    if (initialUserData) {
      console.log('[FLOW] Received initialUserData:', initialUserData)
      setUserData({
        nome: initialUserData.nome || '',
        cpf: initialUserData.cpf || ''
      })
    }
  }, [initialUserData])

  // Notify parent of step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(step)
    }
  }, [step, onStepChange])

  // Notify parent of completion
  useEffect(() => {
    if (finalResult && onComplete) {
      onComplete(finalResult)
    }
  }, [finalResult, onComplete])

  // Notify parent of errors
  useEffect(() => {
    if (error && onErrorCallback) {
      onErrorCallback(new Error(error))
    }
  }, [error, onErrorCallback])

  // Validate and format CPF
  const formatCPF = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '')
    // Limit to 11 digits
    return digits.slice(0, 11)
  }

  // Handle field edit
  const handleFieldEdit = (field, value) => {
    if (field === 'cpf') {
      const formatted = formatCPF(value)
      setUserData(prev => ({ ...prev, cpf: formatted }))
    } else if (field === 'nome') {
      setUserData(prev => ({ ...prev, nome: value }))
    }
  }

  // Validate userData before proceeding
  const validateUserData = () => {
    const errors = []

    if (userData.nome && userData.nome.trim() === '') {
      errors.push('Nome não pode estar vazio')
    }

    if (userData.cpf && userData.cpf.length !== 11) {
      errors.push('CPF deve ter 11 dígitos')
    }

    return errors
  }

  // Step 0: Choose document type
  const handleDocumentTypeChoice = async (type) => {
    try {
      // Consentimentos obrigatórios (defensivo — os botões já ficam desabilitados)
      if (!consentsGiven) {
        return
      }

      // Validate user data if provided
      const validationErrors = validateUserData()
      if (validationErrors.length > 0) {
        setError(validationErrors.join('. '))
        return
      }

      setDocumentType(type)

      // Generate browser fingerprint
      console.log('[FLOW] Generating fingerprint...')
      const fp = await getFingerprint()
      setFingerprint(fp)
      console.log('[FLOW] Fingerprint:', fp.substring(0, 8) + '...')

      // Create verification session with user data and fingerprint
      const sessionId = crypto.randomUUID()
      await createVerification(sessionId, {
        ...userData,
        id_cliente: initialUserData?.id_cliente || null,
        id_billing: initialUserData?.id_billing || null
      }, fp)
      setVerificationId(sessionId)

      setStep(1) // Move to document front capture
    } catch (err) {
      console.error('Failed to create verification:', err)

      // Check if it's a rate limit error (429)
      if (err.response && err.response.status === 429) {
        const errorMsg = err.response.data?.error || 'Limite de tentativas excedido. Por favor, entre em contato com o suporte para continuar.'
        console.error('[FLOW] Rate limit exceeded:', errorMsg)
        setError(errorMsg)
        return
      }

      setError('Erro ao iniciar verificação')
    }
  }

  // Step 1: Document front captured
  const handleDocumentFrontCaptured = async (blob) => {
    console.log('[FLOW] Uploading document front...')

    try {
      const result = await uploadDocumentFront(verificationId, blob, documentType)

      console.log('[FLOW] Document front uploaded:', result)

      // Validar OCR imediatamente (skip if overloaded - human validation will handle it)
      if (!result.ocr || (!result.ocr.success && !result.overloaded)) {
        const errorMsg = result.ocr?.error || 'Erro ao processar'
        console.error('[FLOW] OCR failed:', errorMsg)
        throw new Error('Não foi possível ler o documento. Certifique-se que está nítido, bem iluminado e sem reflexos.')
      }

      if (result.overloaded) {
        console.log('[FLOW] ⚠️  OCR overloaded (frente) - continuing for human validation')
      }

      const extractedData = result.ocr?.data
      console.log('[FLOW] OCR result - Nome:', extractedData?.nome, '- CPF:', extractedData?.cpf)

      // Note: It's OK if nome/cpf are missing - backend will request back capture
      // Validation will happen after merging front + back data
      setDocumentFrontData(result)

      // Check if backend says we can skip back capture
      if (result.skipBackCapture) {
        console.log('[FLOW] All required data extracted from front, skipping back capture')
        // Small delay to ensure camera cleanup before next step
        await new Promise(resolve => setTimeout(resolve, 300))
        setStep(3) // Skip to liveness check (selfie)
      } else {
        console.log('[FLOW] Need to capture back of document')
        // Small delay to ensure camera cleanup before next step
        await new Promise(resolve => setTimeout(resolve, 300))
        setStep(2) // Move to document back
      }
    } catch (err) {
      // Check if it's a validation error (name/CPF mismatch)
      if (err.response && err.response.status === 400 && err.response.data?.data?.rejectionReasons) {
        const reasons = err.response.data.data.rejectionReasons
        const responseData = err.response.data.data
        console.error('[FLOW] Validation failed:', reasons)

        // Save OCR data from the error response so it's available on the result screen
        if (responseData.ocr) {
          setDocumentFrontData({ ocr: responseData.ocr })
        }

        // Show rejection result immediately
        setFinalResult({
          verification: {
            status: 'rejected',
            rejectionReasons: reasons
          },
          faceMatch: null,
          nameMatch: responseData.nameMatch,
          cpfMatch: responseData.cpfMatch,
          approved: false
        })
        setStep(4) // Go to result screen
      } else {
        // Re-throw other errors to be handled by DocumentCapture
        throw err
      }
    }
  }

  // Step 2: Document back captured
  const handleDocumentBackCaptured = async (blob) => {
    try {
      console.log('[FLOW] Uploading document back...')

      const result = await uploadDocumentBack(verificationId, blob, documentType)

      console.log('[FLOW] Document back uploaded:', result)

      // Validar OCR imediatamente (skip if overloaded - human validation will handle it)
      if (!result.ocr || (!result.ocr.success && !result.overloaded)) {
        const errorMsg = result.ocr?.error || 'Erro ao processar'
        console.error('[FLOW] OCR failed:', errorMsg)
        throw new Error('Não foi possível ler o documento. Certifique-se que está nítido, bem iluminado e sem reflexos.')
      }

      if (result.overloaded) {
        console.log('[FLOW] ⚠️  OCR overloaded (verso) - continuing for human validation')
      } else {
        console.log('[FLOW] OCR successful (verso):', result.ocr.data)
      }
      setDocumentBackData(result)

      // Small delay to ensure camera cleanup before next step
      await new Promise(resolve => setTimeout(resolve, 300))
      setStep(3) // Move to liveness check
    } catch (err) {
      console.error('[FLOW] Document back error:', err)

      // Check if it's a validation rejection (HTTP 400 from backend)
      if (err?.response?.status === 400 && err?.response?.data?.data?.rejectionReasons) {
        const responseData = err.response.data.data
        console.log('[FLOW] Validation rejected after back document:', responseData.rejectionReasons)

        // Save OCR data from the error response so it's available on the result screen
        if (responseData.ocr) {
          setDocumentFrontData(prev => prev ? { ...prev, ocr: responseData.ocr } : { ocr: responseData.ocr })
        }

        // Set final result as rejected and jump to result screen
        setFinalResult({
          verification: {
            status: 'rejected',
            rejectionReasons: responseData.rejectionReasons
          },
          faceMatch: null,
          nameMatch: responseData.nameMatch || null,
          cpfMatch: responseData.cpfMatch || null,
          approved: false,
          rejectionReasons: responseData.rejectionReasons
        })

        // Jump to result screen (step 4)
        setStep(4)
        return
      }

      // Other errors - throw to be caught by parent error boundary
      throw err
    }
  }

  // Step 3: Liveness check completed
  const handleLivenessComplete = async (result) => {
    console.log('[FLOW] Liveness check completed:', result)

    setSelfieData(result)

    // Upload selfie for face comparison
    if (result.selfieBlob) {
      try {
        console.log('[FLOW] Uploading selfie...')
        await uploadSelfie(verificationId, result.selfieBlob)
        console.log('[FLOW] Selfie uploaded')
      } catch (err) {
        console.error('[FLOW] Selfie upload error:', err)

        // Check if it's a liveness rejection (HTTP 400 from backend)
        if (err.response && err.response.status === 400 && err.response.data?.data?.liveness) {
          const livenessData = err.response.data.data.liveness
          console.error('[FLOW] Liveness rejected:', livenessData)
          setError('Erro na verificação de vida. Tente novamente em um ambiente bem iluminado.')
          return
        }

        // For network/timeout errors, continue anyway — backend may have processed it
        console.log('[FLOW] Selfie upload failed but continuing to face comparison (backend may have processed)')
      }
    } else {
      console.warn('[FLOW] No selfie blob received from liveness check')
    }

    // Upload liveness GIF - retry once on failure
    if (result.gifBlob) {
      console.log('[FLOW] Uploading liveness GIF...')
      let gifUploaded = false
      for (let attempt = 1; attempt <= 2 && !gifUploaded; attempt++) {
        try {
          await uploadLivenessGIF(verificationId, result.gifBlob)
          console.log('[FLOW] Liveness GIF uploaded successfully')
          gifUploaded = true
        } catch (gifErr) {
          console.error(`[FLOW] Failed to upload GIF (attempt ${attempt}/2):`, gifErr)
          if (attempt < 2) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        }
      }
      if (!gifUploaded) {
        console.warn('[FLOW] GIF upload failed after 2 attempts, continuing without GIF')
      }
    } else {
      console.log('[FLOW] No GIF blob to upload')
    }

    setStep(4) // Move to final comparison

    // Trigger face comparison
    try {
      await handleFaceComparison()
    } catch (err) {
      console.error('[FLOW] Face comparison error:', err)
      // handleFaceComparison has its own error handling with polling,
      // so this should rarely fire. If it does, show a generic error.
      setError('Erro ao processar verificação. Tente novamente.')
    }
  }

  // Step 4: Compare faces
  const handleFaceComparison = async () => {
    try {
      console.log('[FLOW] Comparing faces...')

      const result = await compareFaces(verificationId)
      setFinalResult(result)

      console.log('[FLOW] Face comparison result:', result)
    } catch (err) {
      console.error('[FLOW] compareFaces error:', err?.response?.status, err?.message)

      // Immediate fallback: check status right away (backend might have already processed)
      console.log('[FLOW] Checking status immediately as fallback...')
      try {
        const verification = await getVerificationStatus(verificationId)
        console.log('[FLOW] Immediate status check:', verification.status)

        if (verification.status === 'approved' || verification.status === 'rejected') {
          console.log('[FLOW] ✅ Status already finalized:', verification.status)
          setFinalResult({
            verification,
            faceMatch: verification.faceMatchResult,
            nameMatch: verification.nameMatchResult,
            cpfMatch: verification.cpfMatchResult,
            approved: verification.status === 'approved',
            rejectionReasons: verification.rejectionReasons || []
          })
          return // Success, exit early
        }
      } catch (immediateErr) {
        console.log('[FLOW] Immediate check failed, will start polling...')
      }

      // Polling: tenta buscar status até 5 vezes com intervalo de 1s
      console.log('[FLOW] Starting status polling...')
      let attempts = 0
      const maxAttempts = 5
      const pollInterval = 1000 // 1 segundo (reduzido de 2s)

      const pollStatus = async () => {
        attempts++
        console.log(`[FLOW] Polling attempt ${attempts}/${maxAttempts}`)

        try {
          const verification = await getVerificationStatus(verificationId)
          console.log('[FLOW] Status:', verification.status)

          if (verification.status === 'approved' || verification.status === 'rejected') {
            console.log('[FLOW] Final status retrieved:', verification.status)
            setFinalResult({
              verification,
              faceMatch: verification.faceMatchResult,
              nameMatch: verification.nameMatchResult,
              cpfMatch: verification.cpfMatchResult,
              approved: verification.status === 'approved',
              rejectionReasons: verification.rejectionReasons || []
            })
            return true // Sucesso
          }

          if (attempts < maxAttempts) {
            // Aguarda e tenta novamente (com intervalo crescente: 1s, 2s, 3s, 4s, 5s)
            const waitTime = attempts * 1000
            console.log(`[FLOW] Status still pending, waiting ${waitTime}ms before retry...`)
            await new Promise(resolve => setTimeout(resolve, waitTime))
            return pollStatus()
          } else {
            // Esgotou tentativas
            console.error('[FLOW] Max polling attempts reached, status still:', verification.status)
            setError('Erro ao comparar faces')
            return false
          }
        } catch (statusErr) {
          console.error('[FLOW] Polling error:', statusErr?.message)
          if (attempts < maxAttempts) {
            const waitTime = attempts * 1000
            console.log(`[FLOW] Polling failed, retrying in ${waitTime}ms...`)
            await new Promise(resolve => setTimeout(resolve, waitTime))
            return pollStatus()
          } else {
            console.error('[FLOW] Max polling attempts reached with errors')
            setError('Erro ao comparar faces')
            return false
          }
        }
      }

      await pollStatus()
    }
  }

  const handleRestart = () => {
    setStep(0)
    setVerificationId(null)
    setDocumentType(null)
    setDocumentFrontData(null)
    setDocumentBackData(null)
    setSelfieData(null)
    setFinalResult(null)
    setError(null)
  }

  if (error) {
    return (
      <div className="verification-error">
        <h2>Erro na Verificação</h2>
        <p>{error}</p>
        <button onClick={handleRestart} className="btn-restart">
          Tentar Novamente
        </button>
      </div>
    )
  }

  return (
    <div className="verification-flow">

      {/* Document type selection */}
      {step === 0 && (
        <div className="document-capture-container">
          <div className="document-capture-card">

            <div className="document-header">
              <img
                src="/header-logo2.png"
                alt="BR DID"
                className="document-header-logo"
              />
              <div className="document-header-title">
                <span className="badge-dot"></span>
                VERIFICAÇÃO DE IDENTIDADE
              </div>

              {/* Aviso antifraude — apenas o consentimento da verificação */}
              <div className="fraud-warning-banner">
                <div className="fraud-warning-header">
                  <span className="fraud-warning-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </span>
                  <strong>Atenção: Proteja-se de fraudes</strong>
                </div>

                <label className="consent-checkbox">
                  <input
                    type="checkbox"
                    checked={fraudConsent}
                    onChange={(e) => setFraudConsent(e.target.checked)}
                  />
                  <span className="consent-text">
                    <span className="consent-paragraph">
                      Esta verificação confirma sua identidade para um{' '}
                      <strong>serviço de telefonia que você contratou</strong>.
                    </span>
                    <span className="consent-paragraph">
                      Se você não contratou um serviço da <strong>BR DID</strong>, como
                      Números Virtuais, telefonia ou similar, <strong>não conclua a
                      verificação nem forneça seus dados</strong>, isso pode permitir a
                      ativação de um serviço em seu nome.
                    </span>
                  </span>
                </label>
              </div>

              {/* Consentimento da Política de Tratamento de Dados — fora da caixa de atenção */}
              <label className="consent-checkbox consent-policy">
                <input
                  type="checkbox"
                  checked={policyConsent}
                  onChange={(e) => setPolicyConsent(e.target.checked)}
                />
                <span className="consent-text">
                  Li e estou ciente da{' '}
                  <a
                    href="https://brdid.com.br/legal/politica-de-privacidade"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="consent-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Política de privacidade
                  </a>{' '}
                  e{' '}
                  <a
                    href="https://brdid.com.br/legal/politica-de-processamento-de-dados"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="consent-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Termo de processamento de Dados Pessoais
                  </a>.
                </span>
              </label>
            </div>

            {/* Barra de progresso - Passo 1 */}
            <div className="progress-steps">
              <div className="progress-step current">
                <div className="step-circle">1</div>
                <div className="step-label">DADOS</div>
              </div>

              <div className="progress-line"></div>

              <div className="progress-step">
                <div className="step-circle">2</div>
                <div className="step-label">DOCUMENTO</div>
              </div>

              <div className="progress-line"></div>

              <div className="progress-step">
                <div className="step-circle">3</div>
                <div className="step-label">SELFIE</div>
              </div>

              <div className="progress-line"></div>

              <div className="progress-step">
                <div className="step-circle">4</div>
                <div className="step-label">PRONTO</div>
              </div>
            </div>

            <div className="document-type-content">
              {/* User Data Fields */}
              <div className="user-data-fields">
                {initialUserData?.id_cliente && (
                  <div className="user-data-field">
                    <label className="field-label">Contrato</label>
                    <div className="field-display" style={{ cursor: 'default', opacity: 0.8 }}>
                      {initialUserData.id_cliente}
                    </div>
                  </div>
                )}
                {initialUserData?.id_billing && (
                  <div className="user-data-field">
                    <label className="field-label">Código Assinante</label>
                    <div className="field-display" style={{ cursor: 'default', opacity: 0.8 }}>
                      {initialUserData.id_billing}
                    </div>
                  </div>
                )}

                <div className="user-data-field">
                  <label className="field-label">Nome completo</label>
                  {editingField === 'nome' && !isLocked ? (
                    <input
                      type="text"
                      className="field-input"
                      value={userData.nome}
                      onChange={(e) => handleFieldEdit('nome', e.target.value)}
                      onBlur={() => setEditingField(null)}
                      autoFocus
                      placeholder="Digite seu nome completo"
                    />
                  ) : (
                    <div
                      className={`field-display${!userData.nome ? ' field-display--placeholder' : ''}`}
                      onClick={() => !isLocked && setEditingField('nome')}
                      title={isLocked ? '' : 'Clique para editar'}
                      style={isLocked ? { cursor: 'default', opacity: 0.8 } : {}}
                    >
                      {userData.nome || 'Nome completo igual ao documento'}
                    </div>
                  )}
                </div>

                <div className="user-data-field">
                  <label className="field-label">CPF</label>
                  {editingField === 'cpf' && !isLocked ? (
                    <input
                      type="text"
                      className="field-input"
                      value={userData.cpf}
                      onChange={(e) => handleFieldEdit('cpf', e.target.value)}
                      onBlur={() => setEditingField(null)}
                      autoFocus
                      placeholder="Digite seu CPF (apenas números)"
                      maxLength="11"
                    />
                  ) : (
                    <div
                      className={`field-display${!userData.cpf ? ' field-display--placeholder' : ''}`}
                      onClick={() => !isLocked && setEditingField('cpf')}
                      title={isLocked ? '' : 'Clique para editar'}
                      style={isLocked ? { cursor: 'default', opacity: 0.8 } : {}}
                    >
                      {userData.cpf ?
                        `${userData.cpf.slice(0, 3)}.${userData.cpf.slice(3, 6)}.${userData.cpf.slice(6, 9)}-${userData.cpf.slice(9, 11)}`
                        : 'CPF igual ao do documento'}
                    </div>
                  )}
                </div>
              </div>

              <h3 className="document-type-title">Selecione seu documento</h3>
              {!consentsGiven && (
                <p className="consent-hint">
                  Marque como lido as caixas de seleção no topo da página.
                </p>
              )}
              <div className="document-options">
                <button
                  onClick={() => handleDocumentTypeChoice('CNH')}
                  className="btn-document-type"
                  disabled={!consentsGiven}
                >
                  <div className="icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 13.5 4.6 9a2 2 0 0 1 1.9-1.3h11a2 2 0 0 1 1.9 1.3L21 13.5v4.5a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
                      <line x1="3" y1="13.5" x2="21" y2="13.5" />
                      <circle cx="7" cy="16" r="1" />
                      <circle cx="17" cy="16" r="1" />
                    </svg>
                  </div>
                  <div className="label">CNH</div>
                  <div className="description">Carteira Nacional de Habilitação</div>
                </button>
                <button
                  onClick={() => handleDocumentTypeChoice('RG')}
                  className="btn-document-type"
                  disabled={!consentsGiven}
                >
                  <div className="icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="16" rx="2" />
                      <circle cx="8.5" cy="10" r="2" />
                      <path d="M5.5 16a3 3 0 0 1 6 0" />
                      <line x1="14.5" y1="9" x2="18" y2="9" />
                      <line x1="14.5" y1="12" x2="18" y2="12" />
                      <line x1="14.5" y1="15" x2="18" y2="15" />
                    </svg>
                  </div>
                  <div className="label">RG</div>
                  <div className="description">Registro Geral</div>
                </button>
              </div>
            </div>

            <div className="document-footer">
              <div className="footer-security">
                <svg className="footer-security-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Seus dados são protegidos e criptografados
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Capture document front */}
      {step === 1 && (
        USE_SIMPLE_CAPTURE ? (
          <DocumentCaptureSimple
            key="front"
            documentType={`Frente da ${documentType}`}
            onCapture={handleDocumentFrontCaptured}
          />
        ) : (
          <DocumentCapture
            key="front"
            documentType={`Frente da ${documentType}`}
            onCapture={handleDocumentFrontCaptured}
          />
        )
      )}

      {/* Step 2: Capture document back */}
      {step === 2 && (
        USE_SIMPLE_CAPTURE ? (
          <DocumentCaptureSimple
            key="back"
            documentType={`Verso da ${documentType}`}
            onCapture={handleDocumentBackCaptured}
          />
        ) : (
          <DocumentCapture
            key="back"
            documentType={`Verso da ${documentType}`}
            onCapture={handleDocumentBackCaptured}
          />
        )
      )}

      {/* Step 3: Liveness check */}
      {step === 3 && (
        <LivenessCheck onComplete={handleLivenessComplete} />
      )}

      {/* Step 4: Final result */}
      {step === 4 && finalResult && (
        <VerificationResult
          result={finalResult}
          documentData={documentFrontData}
          userData={userData}
          onRestart={handleRestart}
        />
      )}

      {/* Loading state for step 4 */}
      {step === 4 && !finalResult && (
        <div className="loading-comparison">
          <div className="spinner"></div>
          <p>Fazendo reconhecimento facial...</p>
          <p className="small">Isso pode levar alguns segundos</p>
        </div>
      )}
    </div>
  )
}

export default VerificationFlow
